import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLock,
  FaSpinner,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { shelfListingService } from '../../../../services/shelfListingService';
import { PRICING_MODES, PROVENANCE_PRICE_THRESHOLD_INR } from '../../../../utils/shelfListingConstants';
import { authService } from '../../../../services/apiService';
import ShelfListingStepper from '../components/ShelfListingStepper';

const GRADE_COLORS = { A: '#059669', B: '#2563eb', C: '#d97706' };

function formatInr(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `₹${Math.round(Number(n)).toLocaleString('en-IN')}`;
}

function HighPriceConfirmModal({ open, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-6 space-y-4">
        <FaExclamationTriangle className="text-amber-500 text-2xl" />
        <p className="text-sm text-slate-700">{message}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2 rounded-lg border border-slate-300 text-sm font-medium"
          >
            Adjust price
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold"
          >
            Yes, publish anyway
          </button>
        </div>
      </div>
    </div>
  );
}

function PricingModeCard({ mode, selected, onSelect, children }) {
  return (
    <label
      className={`block rounded-xl border-2 p-4 cursor-pointer transition-all ${
        selected ? 'border-emerald-600 bg-emerald-50/50' : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <input
          type="radio"
          name="pricing-mode"
          checked={selected}
          onChange={() => onSelect(mode.id)}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900">{mode.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{mode.description}</p>
          {selected && children ? <div className="mt-3">{children}</div> : null}
        </div>
      </div>
    </label>
  );
}

export default function ShelfPartPricingPublish() {
  const navigate = useNavigate();
  const location = useLocation();
  const draft = location.state || {};
  const {
    shots = [],
    sessionId,
    partName = '',
    vehicle = {},
    fitmentAttrs = {},
    grade = '',
    profile = 'B',
    description = '',
    availabilityTier = 'LIKELY',
    isOemOriginal = false,
    oemPhotoUrl,
    oemClaimAttempt = 1,
    provenancePack,
    provenanceRequired = false,
  } = draft;

  const [pricingMode, setPricingMode] = useState('');
  const [price, setPrice] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [market, setMarket] = useState(null);
  const [loadingMarket, setLoadingMarket] = useState(true);
  const [priceError, setPriceError] = useState('');
  const [needsHighPriceConfirm, setNeedsHighPriceConfirm] = useState(false);
  const [confirmHighPrice, setConfirmHighPrice] = useState(false);
  const [showHighPriceModal, setShowHighPriceModal] = useState(false);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [draftSaved, setDraftSaved] = useState(null);
  const [provenanceSourceUrl, setProvenanceSourceUrl] = useState(provenancePack?.sourceDocumentUrl || '');
  const [provenanceVinUrl, setProvenanceVinUrl] = useState(provenancePack?.vinEvidenceUrl || '');
  const [sellerDeclaration, setSellerDeclaration] = useState(
    Boolean(provenancePack?.sellerDeclarationSigned)
  );
  const checkTimer = useRef(null);

  const imageUrls = shots.map((s) => s.imageUrl || s.previewUrl).filter(Boolean);

  const loadMarket = useCallback(async () => {
    try {
      setLoadingMarket(true);
      const data = await shelfListingService.marketReference({
        partCategory: partName,
        partName,
        vehicle,
        usedGrade: grade,
      });
      setMarket(data);
    } catch {
      toast.error('Could not load market reference');
    } finally {
      setLoadingMarket(false);
    }
  }, [partName, vehicle, grade]);

  useEffect(() => {
    if (partName && grade) loadMarket();
  }, [partName, grade, loadMarket]);

  const runPriceCheck = useCallback(
    async (opts = {}) => {
      if (!pricingMode || pricingMode === 'contact') {
        setPriceError('');
        setNeedsHighPriceConfirm(false);
        return true;
      }
      setChecking(true);
      try {
        await shelfListingService.priceCheck({
          partCategory: partName,
          partName,
          vehicle,
          usedGrade: grade,
          pricingMode,
          price: price !== '' ? Number(price) : undefined,
          priceMin: priceMin !== '' ? Number(priceMin) : undefined,
          priceMax: priceMax !== '' ? Number(priceMax) : undefined,
          sessionId,
          confirmHighPrice: opts.confirmHighPrice || confirmHighPrice,
        });
        setPriceError('');
        setNeedsHighPriceConfirm(false);
        return true;
      } catch (err) {
        const data = err?.response?.data;
        if (data?.hardBlock) {
          setPriceError(data.message);
          setNeedsHighPriceConfirm(false);
        } else if (data?.requiresConfirmation) {
          setNeedsHighPriceConfirm(true);
          setPriceError('');
        } else {
          setPriceError(data?.message || 'Price check failed');
        }
        return false;
      } finally {
        setChecking(false);
      }
    },
    [pricingMode, partName, vehicle, grade, price, priceMin, priceMax, sessionId, confirmHighPrice]
  );

  useEffect(() => {
    if (!pricingMode || pricingMode === 'contact') return;
    if (pricingMode === 'reference' && !market?.marketRef) return;
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(() => runPriceCheck(), 500);
    return () => clearTimeout(checkTimer.current);
  }, [pricingMode, price, priceMin, priceMax, market?.marketRef, runPriceCheck]);

  const effectivePrice = useMemo(() => {
    if (pricingMode === 'contact') return 0;
    if (pricingMode === 'reference') return Number(market?.marketRef) || 0;
    if (pricingMode === 'range') return Number(priceMax) || 0;
    return Number(price) || 0;
  }, [pricingMode, price, priceMax, market?.marketRef]);

  const provenanceNeeded =
    provenanceRequired || effectivePrice >= PROVENANCE_PRICE_THRESHOLD_INR;

  const pricingDisplay = useMemo(() => {
    if (!pricingMode) return null;
    const mode = PRICING_MODES.find((m) => m.id === pricingMode);
    if (pricingMode === 'contact') return mode?.displayLabel || 'Contact for price';
    if (pricingMode === 'reference') return `${mode?.displayLabel || 'Market price'} — ${formatInr(market?.marketRef)}`;
    if (pricingMode === 'range') return `${formatInr(priceMin)} – ${formatInr(priceMax)}`;
    return formatInr(price);
  }, [pricingMode, price, priceMin, priceMax, market?.marketRef]);

  const fitmentEntries = Object.entries(fitmentAttrs || {}).filter(([, v]) => String(v ?? '').trim());

  const fireConfetti = () => {
    confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
    setTimeout(() => confetti({ particleCount: 40, spread: 80, origin: { y: 0.5 } }), 200);
  };

  const doPublish = async (withHighPriceConfirm = false) => {
    setSubmitting(true);
    try {
      const res = await shelfListingService.publish({
        shots,
        sessionId,
        partName: partName.trim(),
        partCategory: partName.trim(),
        vehicle,
        fitmentAttrs,
        usedGrade: grade,
        pricingMode,
        price: pricingMode === 'fixed' ? Number(price) : undefined,
        priceMin: pricingMode === 'range' ? Number(priceMin) : undefined,
        priceMax: pricingMode === 'range' ? Number(priceMax) : undefined,
        description,
        availabilityTier,
        isOemOriginal,
        oemPhotoUrl: isOemOriginal ? oemPhotoUrl : undefined,
        oemClaimAttempt,
        provenancePack: provenanceNeeded
          ? {
              sourceDocumentUrl: provenanceSourceUrl,
              vinEvidenceUrl: provenanceVinUrl || undefined,
              sellerDeclarationSigned: sellerDeclaration,
            }
          : undefined,
        listingPhotoSessionId: sessionId,
        confirmHighPrice: withHighPriceConfirm || confirmHighPrice,
      });

      const data = res?.data || res;
      setPublishResult(data);
      if (data?.status === 'live' || res?.success) {
        fireConfetti();
        toast.success(data?.message || 'Your listing is live');
      } else if (data?.provenanceReviewRequired) {
        toast.info(data.message);
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.savedAsDraft) {
        setDraftSaved(data.data || data);
        toast.warn(data.message || 'Saved as draft — resolve gate flags');
        return;
      }
      if (data?.requiresConfirmation) {
        setShowHighPriceModal(true);
        return;
      }
      if (data?.hardBlock) {
        setPriceError(data.message);
      }
      toast.error(data?.message || err.message || 'Publish failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!pricingMode) {
      toast.error('Select a pricing mode');
      return;
    }
    if (pricingMode === 'fixed' && (!price || Number(price) <= 0)) {
      toast.error('Enter a valid price');
      return;
    }
    if (pricingMode === 'range') {
      if (!priceMin || !priceMax || Number(priceMin) <= 0 || Number(priceMax) <= 0) {
        toast.error('Enter valid min and max prices');
        return;
      }
      if (Number(priceMin) > Number(priceMax)) {
        toast.error('Min cannot exceed max');
        return;
      }
    }
    if (priceError) {
      toast.error('Fix price issues before publishing');
      return;
    }
    if (provenanceNeeded) {
      if (!provenanceSourceUrl) {
        toast.error('Source document required for this listing');
        return;
      }
      if (!sellerDeclaration) {
        toast.error('Seller declaration is required');
        return;
      }
    }
    const ok = await runPriceCheck();
    if (!ok) {
      if (needsHighPriceConfirm) setShowHighPriceModal(true);
      return;
    }
    if (needsHighPriceConfirm && !confirmHighPrice) {
      setShowHighPriceModal(true);
      return;
    }
    await doPublish(confirmHighPrice);
  };

  const uploadDoc = async (file, setter) => {
    try {
      const url = await authService.uploadCompressedFile(file);
      setter(url?.path || url?.url || url);
    } catch {
      toast.error('Upload failed');
    }
  };

  if (!shots.length || !partName || !grade) {
    return (
      <div className="max-w-md mx-auto py-16 text-center px-4">
        <p className="text-slate-600">Complete part details first.</p>
        <button
          type="button"
          onClick={() => navigate('/vendor/dashboard/parts/add/details')}
          className="mt-4 text-emerald-700 font-semibold text-sm"
        >
          Back to details →
        </button>
      </div>
    );
  }

  if (publishResult && !draftSaved) {
    const live = publishResult.status === 'live';
    return (
      <div className="max-w-lg mx-auto py-12 px-4 text-center space-y-5">
        <ShelfListingStepper current={3} />
        <FaCheckCircle className={`text-5xl mx-auto ${live ? 'text-emerald-600' : 'text-amber-500'}`} />
        <h2 className="text-2xl font-bold text-slate-900">
          {live ? 'Your listing is live' : publishResult.provenanceReviewRequired ? 'Listing under review' : 'Listing submitted'}
        </h2>
        <p className="text-sm text-slate-600">{publishResult.message}</p>
        {publishResult.photoVersionId && (
          <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
            <FaLock className="text-emerald-600" /> Photos locked — version {publishResult.photoVersionId.slice(0, 8)}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          {publishResult.viewListingUrl && (
            <Link
              to={publishResult.viewListingUrl}
              className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold"
            >
              View listing
            </Link>
          )}
          <button
            type="button"
            onClick={() => navigate('/vendor/dashboard/products')}
            className="px-6 py-2.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700"
          >
            Back to products
          </button>
        </div>
      </div>
    );
  }

  if (draftSaved) {
    return (
      <div className="max-w-lg mx-auto py-12 px-4 space-y-4">
        <FaExclamationTriangle className="text-amber-500 text-3xl" />
        <h2 className="text-xl font-bold text-slate-900">Saved as draft</h2>
        <p className="text-sm text-slate-600">{draftSaved.message}</p>
        <ul className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
          {(draftSaved.gateFlags || []).map((f, i) => (
            <li key={i}>• {f.message}</li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => {
            setDraftSaved(null);
            setConfirmHighPrice(true);
            setShowHighPriceModal(true);
          }}
          className="text-sm text-emerald-700 font-semibold"
        >
          Resolve and re-submit →
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-4 px-4 pb-28">
      <HighPriceConfirmModal
        open={showHighPriceModal}
        message="Are you sure? This is significantly above market price."
        onConfirm={async () => {
          setConfirmHighPrice(true);
          setShowHighPriceModal(false);
          await doPublish(true);
        }}
        onCancel={() => setShowHighPriceModal(false)}
      />

      <button
        type="button"
        onClick={() => navigate('/vendor/dashboard/parts/add/details', { state: draft })}
        className="text-sm text-slate-500 hover:text-slate-800 mb-3 flex items-center gap-1"
      >
        <FaArrowLeft /> Edit part details
      </button>

      <ShelfListingStepper current={3} />

      <h2 className="text-xl font-bold text-slate-900">Pricing & publish</h2>
      <p className="text-sm text-slate-500 mt-1">Click 3 — set pricing and publish to catalog</p>

      {/* Publish summary card */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-bold text-slate-900">{partName}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {vehicle.make} {vehicle.model} · {vehicle.generation || vehicle.year}
            </p>
          </div>
          {grade && (
            <span
              className="shrink-0 px-2.5 py-1 rounded-lg text-xs font-bold text-white"
              style={{ backgroundColor: GRADE_COLORS[grade] || '#64748b' }}
            >
              Grade {grade}
            </span>
          )}
        </div>

        {pricingMode && (
          <p className="text-sm text-slate-700">
            <span className="font-semibold">Pricing:</span> {pricingDisplay}
          </p>
        )}

        {fitmentEntries.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {fitmentEntries.map(([k, v]) => (
              <span key={k} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {k}: {v}
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2 overflow-x-auto pb-1">
          {imageUrls.map((url, i) => (
            <div key={i} className="relative shrink-0">
              <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
              <FaLock className="absolute bottom-1 right-1 text-[8px] text-white drop-shadow" title="Locked on publish" />
            </div>
          ))}
        </div>
      </div>

      {/* Pricing modes */}
      <section className="mt-6 space-y-3">
        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Pricing mode *</h3>
        {PRICING_MODES.map((mode) => (
          <PricingModeCard
            key={mode.id}
            mode={mode}
            selected={pricingMode === mode.id}
            onSelect={setPricingMode}
          >
            {mode.id === 'fixed' && (
              <input
                type="number"
                min="1"
                placeholder="Enter price (₹)"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  setConfirmHighPrice(false);
                }}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            )}
            {mode.id === 'range' && (
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  min="1"
                  placeholder="Min (₹)"
                  value={priceMin}
                  onChange={(e) => {
                    setPriceMin(e.target.value);
                    setConfirmHighPrice(false);
                  }}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  min="1"
                  placeholder="Max (₹)"
                  value={priceMax}
                  onChange={(e) => {
                    setPriceMax(e.target.value);
                    setConfirmHighPrice(false);
                  }}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            )}
            {mode.id === 'reference' && (
              <p className="text-sm font-semibold text-emerald-700">
                {loadingMarket ? (
                  <span className="flex items-center gap-1 text-slate-500 font-normal">
                    <FaSpinner className="animate-spin" /> Loading market price…
                  </span>
                ) : (
                  <>Market price: {formatInr(market?.marketRef)}</>
                )}
              </p>
            )}
          </PricingModeCard>
        ))}
        {!pricingMode && <p className="text-xs text-red-600">Select a pricing mode to publish</p>}
      </section>

      {/* Market band hint */}
      {market && pricingMode && pricingMode !== 'contact' && (
        <p className="mt-3 text-xs text-slate-500">
          Market band: {formatInr(market.marketFloor)} – {formatInr(market.marketCeiling)}
          {checking && <FaSpinner className="inline ml-2 animate-spin" />}
        </p>
      )}

      {priceError && (
        <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
          <FaExclamationTriangle /> {priceError}
        </p>
      )}

      {needsHighPriceConfirm && !priceError && (
        <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          Price is significantly above market — confirmation required on publish.
        </p>
      )}

      {provenanceNeeded && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
            <FaExclamationTriangle /> Enhanced Provenance Pack required
          </p>
          <p className="text-xs text-amber-800">
            {provenanceRequired
              ? 'Restricted category — admin review before listing goes live.'
              : `Price ≥ ₹${PROVENANCE_PRICE_THRESHOLD_INR.toLocaleString('en-IN')} — admin review required.`}
          </p>
          <label className="block text-xs font-semibold text-slate-700">
            Source document *
            <input
              type="file"
              accept="image/*,application/pdf"
              className="mt-1 block w-full text-xs"
              onChange={(e) => uploadDoc(e.target.files[0], setProvenanceSourceUrl)}
            />
          </label>
          <label className="block text-xs font-semibold text-slate-700">
            VIN / chassis evidence (if applicable)
            <input
              type="file"
              accept="image/*,application/pdf"
              className="mt-1 block w-full text-xs"
              onChange={(e) => uploadDoc(e.target.files[0], setProvenanceVinUrl)}
            />
          </label>
          <label className="flex items-start gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={sellerDeclaration}
              onChange={(e) => setSellerDeclaration(e.target.checked)}
            />
            I declare this part was legally sourced and the information provided is accurate.
          </label>
          <p className="text-xs text-slate-600 italic">
            Listing under review. Usually approved within 24h.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={handlePublish}
        disabled={submitting || !pricingMode || !!priceError}
        className="mt-8 w-full py-3.5 rounded-xl bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 sticky bottom-4 shadow-lg"
      >
        {submitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
        Publish listing
      </button>
    </div>
  );
}
